import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useRequestQuipteamsSupport } from '@/hooks/useDevices';
import { useQuipteamsOffices } from '@/hooks/useApprovals';
import type { Device, QuipteamsActionType } from '../../../../shared/types';

type SideMode = 'manual' | 'office';

interface QuipteamsSupportDialogProps {
  device: Device | null;
  open: boolean;
  onClose: () => void;
}

const ACTION_LABELS: Record<QuipteamsActionType, string> = {
  Repair: 'Reparación',
  Reassign: 'Reasignación',
  Retire: 'Retiro',
};

interface RepairFields {
  problemDescription: string;
  urgency: 'low' | 'medium' | 'high';
}

interface ReassignFields {
  // Current owner (person/office who currently has the device)
  currentOwnerName: string;
  currentOwnerEmail: string;
  currentOwnerId: string;
  currentOwnerAddress: string;
  currentOwnerAddressExtra: string;
  currentOwnerPhone: string;
  currentOwnerWhenToContact: string;
  // New recipient
  recipientName: string;
  recipientEmail: string;
  recipientId: string;
  recipientCountry: string;
  recipientAddress: string;
  recipientAddressExtra: string;
  recipientPhone: string;
  recipientWhenToContact: string;
  devicePin: string;
}

interface RetireFields {
  retireReason: string;
}

const URGENCY_LABELS = { low: 'Baja', medium: 'Media', high: 'Alta' };

export function QuipteamsSupportDialog({ device, open, onClose }: QuipteamsSupportDialogProps) {
  const { t } = useTranslation();
  const [actionType, setActionType] = useState<QuipteamsActionType>('Repair');
  const [actionId, setActionId] = useState<string | null>(null);

  const [repair, setRepair] = useState<RepairFields>({ problemDescription: '', urgency: 'medium' });
  const [reassign, setReassign] = useState<ReassignFields>({
    currentOwnerName: '', currentOwnerEmail: '', currentOwnerId: '', currentOwnerAddress: '', currentOwnerAddressExtra: '',
    currentOwnerPhone: '', currentOwnerWhenToContact: '',
    recipientName: '', recipientEmail: '', recipientId: '', recipientCountry: 'Argentina',
    recipientAddress: '', recipientAddressExtra: '', recipientPhone: '', recipientWhenToContact: '',
    devicePin: '',
  });
  const [retire, setRetire] = useState<RetireFields>({ retireReason: '' });

  // Oficina ↔ Manual por cada parte (usuario actual / nuevo destinatario)
  const [currentMode, setCurrentMode] = useState<SideMode>('manual');
  const [currentOfficeId, setCurrentOfficeId] = useState('');
  const [recipientMode, setRecipientMode] = useState<SideMode>('manual');
  const [recipientOfficeId, setRecipientOfficeId] = useState('');

  const mutation = useRequestQuipteamsSupport();

  const officesQuery = useQuipteamsOffices(actionType === 'Reassign');
  const offices = officesQuery.data?.offices ?? [];
  const fmtPhone = (o?: { phone?: string; phoneCountryCode?: string }) =>
    o?.phone ? `${o.phoneCountryCode ? o.phoneCountryCode + ' ' : ''}${o.phone}` : '';

  const selectCurrentOffice = (id: string) => {
    setCurrentOfficeId(id);
    const o = offices.find((x) => x.id === id);
    if (o) setReassign((p) => ({
      ...p,
      currentOwnerName: o.contactName || o.name,
      currentOwnerAddress: o.address,
      currentOwnerPhone: fmtPhone(o),
    }));
  };
  const selectRecipientOffice = (id: string) => {
    setRecipientOfficeId(id);
    const o = offices.find((x) => x.id === id);
    if (o) setReassign((p) => ({
      ...p,
      recipientAddress: o.address,
      recipientCountry: o.country,
      recipientPhone: fmtPhone(o),
      recipientName: o.contactName || o.name,
    }));
  };

  const officeToggle = (mode: SideMode, onMode: (m: SideMode) => void) => (
    <div className="flex gap-2 mb-3">
      <Button type="button" size="sm" variant={mode === 'office' ? 'default' : 'outline'} className="h-7 text-xs"
        onClick={() => onMode('office')}>Oficina</Button>
      <Button type="button" size="sm" variant={mode === 'manual' ? 'default' : 'outline'} className="h-7 text-xs"
        onClick={() => onMode('manual')}>Manual</Button>
    </div>
  );

  const officeSelect = (value: string, onSelect: (id: string) => void) => {
    const sel = offices.find((o) => o.id === value) || null;
    return (
      <div className="space-y-1.5 mb-3">
        <Select value={value} onValueChange={onSelect}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder={officesQuery.isLoading ? t('devices.quipteams.cargando_oficinas') : t('devices.quipteams.elegi_una_oficina')} />
          </SelectTrigger>
          <SelectContent>
            {offices.map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.name} — {o.country}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {sel && (
          <div className="rounded bg-muted/40 border p-2 text-xs text-muted-foreground space-y-0.5">
            <div>📍 {sel.address}</div>
            {sel.contactName && <div>👤 {sel.contactName}</div>}
            {sel.phone && <div>📞 {fmtPhone(sel)}</div>}
          </div>
        )}
        {!officesQuery.isLoading && offices.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('devices.quipteams.no_hay_oficinas_configuradas_usa_manual')}</p>
        )}
      </div>
    );
  };

  function handleClose() {
    setActionType('Repair');
    setActionId(null);
    setRepair({ problemDescription: '', urgency: 'medium' });
    setReassign({
      currentOwnerName: '', currentOwnerEmail: '', currentOwnerId: '', currentOwnerAddress: '', currentOwnerAddressExtra: '',
      currentOwnerPhone: '', currentOwnerWhenToContact: '',
      recipientName: '', recipientEmail: '', recipientId: '', recipientCountry: 'Argentina',
      recipientAddress: '', recipientAddressExtra: '', recipientPhone: '', recipientWhenToContact: '',
      devicePin: '',
    });
    setRetire({ retireReason: '' });
    setCurrentMode('manual'); setCurrentOfficeId('');
    setRecipientMode('manual'); setRecipientOfficeId('');
    onClose();
  }

  function isValid() {
    if (actionType === 'Repair') return repair.problemDescription.trim().length > 0;
    if (actionType === 'Reassign') return (
      // En modo oficina el usuario actual es una oficina (sin email): el backend cae al solicitante.
      (currentMode === 'office' || reassign.currentOwnerEmail.trim().length > 0) &&
      reassign.recipientName.trim().length > 0 &&
      reassign.recipientEmail.trim().length > 0 &&
      reassign.recipientCountry.trim().length > 0 &&
      reassign.recipientAddress.trim().length > 0
    );
    if (actionType === 'Retire') return retire.retireReason.trim().length > 0;
    return false;
  }

  async function handleSubmit() {
    if (!device || !isValid()) return;
    const result = await mutation.mutateAsync({
      deviceId: device.deviceId,
      actionType,
      ...(actionType === 'Repair' && { repair }),
      ...(actionType === 'Reassign' && {
        reassign: {
          // El nombre de la oficina solo se manda como dueño actual en modo oficina.
          currentOwnerName: currentMode === 'office' ? (reassign.currentOwnerName || undefined) : undefined,
          currentOwnerEmail: reassign.currentOwnerEmail || undefined,
          currentOwnerId: reassign.currentOwnerId || undefined,
          currentOwnerAddress: reassign.currentOwnerAddress || undefined,
          currentOwnerAddressExtra: reassign.currentOwnerAddressExtra || undefined,
          currentOwnerPhone: reassign.currentOwnerPhone || undefined,
          currentOwnerWhenToContact: reassign.currentOwnerWhenToContact || undefined,
          recipientName: reassign.recipientName,
          recipientEmail: reassign.recipientEmail,
          recipientId: reassign.recipientId || undefined,
          recipientCountry: reassign.recipientCountry,
          recipientAddress: reassign.recipientAddress,
          recipientAddressExtra: reassign.recipientAddressExtra || undefined,
          recipientPhone: reassign.recipientPhone || undefined,
          recipientWhenToContact: reassign.recipientWhenToContact || undefined,
          devicePin: reassign.devicePin || undefined,
        },
      }),
      ...(actionType === 'Retire' && { retire }),
    });
    setActionId(result.actionId);
  }

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('devices.quipteams.dialog_title')}</DialogTitle>
          <div className="text-sm text-muted-foreground mt-1">
            {device.modelo} · {device.nroSerie}
          </div>
          {device.quipteamsOrderId && (
            <Badge variant="secondary" className="w-fit mt-1">
              {t('devices.quipteams.managed_badge')}
            </Badge>
          )}
        </DialogHeader>

        {actionId ? (
          <div className="py-4 space-y-2">
            <p className="text-sm font-medium text-green-700">{t('devices.quipteams.success_message')}</p>
            <p className="text-xs text-muted-foreground">
              {t('devices.quipteams.action_id_label')} <span className="font-mono font-medium">{actionId}</span>
            </p>
            <p className="text-xs text-muted-foreground">{t('devices.quipteams.quipteams_will_contact')}</p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Tipo de acción */}
            <div className="space-y-2">
              <Label>{t('devices.quipteams.action_type_label')}</Label>
              <Select value={actionType} onValueChange={(v) => setActionType(v as QuipteamsActionType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ACTION_LABELS) as QuipteamsActionType[]).map((type) => (
                    <SelectItem key={type} value={type}>{t(`devices.quipteams.action_${type.toLowerCase()}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reparación */}
            {actionType === 'Repair' && (
              <>
                <div className="space-y-2">
                  <Label>{t('devices.quipteams.repair_desc_label')} <span className="text-destructive">*</span></Label>
                  <Textarea
                    placeholder={t('devices.quipteams.repair_desc_placeholder')}
                    value={repair.problemDescription}
                    onChange={(e) => setRepair(p => ({ ...p, problemDescription: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('devices.quipteams.urgency_label')}</Label>
                  <Select value={repair.urgency} onValueChange={(v) => setRepair(p => ({ ...p, urgency: v as RepairFields['urgency'] }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(URGENCY_LABELS) as (keyof typeof URGENCY_LABELS)[]).map((u) => (
                        <SelectItem key={u} value={u}>{t(`devices.quipteams.urgency_${u}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Reasignación */}
            {actionType === 'Reassign' && (
              <div className="space-y-4">
                {/* Usuario actual */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('devices.quipteams.current_user_title')}</p>
                  {officeToggle(currentMode, setCurrentMode)}
                  {currentMode === 'office' && officeSelect(currentOfficeId, selectCurrentOffice)}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.email_label')} {currentMode === 'manual' && <span className="text-destructive">*</span>}</Label>
                        <Input
                          type="email"
                          placeholder="usuario@empresa.com"
                          value={reassign.currentOwnerEmail}
                          onChange={(e) => setReassign(p => ({ ...p, currentOwnerEmail: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.id_label')}</Label>
                        <Input
                          placeholder="12345678"
                          value={reassign.currentOwnerId}
                          onChange={(e) => setReassign(p => ({ ...p, currentOwnerId: e.target.value }))}
                        />
                      </div>
                    </div>
                    {currentMode === 'manual' && (
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.address_label')}</Label>
                        <Input
                          placeholder={t('devices.quipteams.av_corrientes_1234_caba')}
                          value={reassign.currentOwnerAddress}
                          onChange={(e) => setReassign(p => ({ ...p, currentOwnerAddress: e.target.value }))}
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>{t('devices.quipteams.address_extra_label')}</Label>
                      <Input
                        placeholder={t('devices.quipteams.piso_3_dpto_b')}
                        value={reassign.currentOwnerAddressExtra}
                        onChange={(e) => setReassign(p => ({ ...p, currentOwnerAddressExtra: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {currentMode === 'manual' && (
                        <div className="space-y-2">
                          <Label>{t('devices.quipteams.phone_label')}</Label>
                          <Input
                            placeholder="+54 11 1234-5678"
                            value={reassign.currentOwnerPhone}
                            onChange={(e) => setReassign(p => ({ ...p, currentOwnerPhone: e.target.value }))}
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.when_to_contact_label')}</Label>
                        <Input
                          placeholder={t('devices.quipteams.lunes_a_viernes_9_18hs')}
                          value={reassign.currentOwnerWhenToContact}
                          onChange={(e) => setReassign(p => ({ ...p, currentOwnerWhenToContact: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('devices.quipteams.new_recipient_title')}</p>
                  {officeToggle(recipientMode, setRecipientMode)}
                  {recipientMode === 'office' && officeSelect(recipientOfficeId, selectRecipientOffice)}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.name_label')} <span className="text-destructive">*</span></Label>
                        <Input
                          placeholder={t('devices.quipteams.juan_perez')}
                          value={reassign.recipientName}
                          onChange={(e) => setReassign(p => ({ ...p, recipientName: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.email_label')} <span className="text-destructive">*</span></Label>
                        <Input
                          type="email"
                          placeholder="juan@empresa.com"
                          value={reassign.recipientEmail}
                          onChange={(e) => setReassign(p => ({ ...p, recipientEmail: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.id_label')}</Label>
                        <Input
                          placeholder="12345678"
                          value={reassign.recipientId}
                          onChange={(e) => setReassign(p => ({ ...p, recipientId: e.target.value }))}
                        />
                      </div>
                      {recipientMode === 'manual' && (
                        <div className="space-y-2">
                          <Label>{t('devices.quipteams.country_label')} <span className="text-destructive">*</span></Label>
                          <Input
                            placeholder="Argentina"
                            value={reassign.recipientCountry}
                            onChange={(e) => setReassign(p => ({ ...p, recipientCountry: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                    {recipientMode === 'manual' && (
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.delivery_address_label')} <span className="text-destructive">*</span></Label>
                        <Input
                          placeholder={t('devices.quipteams.av_corrientes_1234_caba')}
                          value={reassign.recipientAddress}
                          onChange={(e) => setReassign(p => ({ ...p, recipientAddress: e.target.value }))}
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>{t('devices.quipteams.address_extra_label')}</Label>
                      <Input
                        placeholder={t('devices.quipteams.piso_3_dpto_b')}
                        value={reassign.recipientAddressExtra}
                        onChange={(e) => setReassign(p => ({ ...p, recipientAddressExtra: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {recipientMode === 'manual' && (
                        <div className="space-y-2">
                          <Label>{t('devices.quipteams.phone_label')}</Label>
                          <Input
                            placeholder="+54 11 1234-5678"
                            value={reassign.recipientPhone}
                            onChange={(e) => setReassign(p => ({ ...p, recipientPhone: e.target.value }))}
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>{t('devices.quipteams.when_to_contact_label')}</Label>
                        <Input
                          placeholder={t('devices.quipteams.lunes_a_viernes_9_18hs')}
                          value={reassign.recipientWhenToContact}
                          onChange={(e) => setReassign(p => ({ ...p, recipientWhenToContact: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="space-y-2">
                    <Label>{t('devices.quipteams.device_pin_label')} <span className="text-muted-foreground text-xs">{t('devices.quipteams.device_pin_optional')}</span></Label>
                    <Input
                      placeholder={t('devices.quipteams.device_pin_placeholder')}
                      value={reassign.devicePin}
                      onChange={(e) => setReassign(p => ({ ...p, devicePin: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Retiro */}
            {actionType === 'Retire' && (
              <div className="space-y-2">
                <Label>{t('devices.quipteams.retire_reason_label')} <span className="text-destructive">*</span></Label>
                <Textarea
                  placeholder={t('devices.quipteams.retire_reason_placeholder')}
                  value={retire.retireReason}
                  onChange={(e) => setRetire({ retireReason: e.target.value })}
                  rows={3}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {actionId ? (
            <Button onClick={handleClose}>{t('common.ui.close')}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={mutation.isPending}>
                {t('common.ui.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={mutation.isPending || !isValid()}>
                {mutation.isPending ? t('devices.quipteams.sending') : t('devices.quipteams.submit_btn')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
